import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, inject, isDevMode, provideAppInitializer } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { provideAnimations } from '@angular/platform-browser/animations';
import { NavigationError, provideRouter, withComponentInputBinding, withInMemoryScrolling, withNavigationErrorHandler } from '@angular/router';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { provideToastr } from 'ngx-toastr';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/providers/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { AppVersionService } from 'app/core/services/app-version.service';
import { authInterceptor } from './core/interceptor/auth.interceptor';
import { loadingInterceptor } from './core/interceptor/loading.interceptor';
import { RuntimeProtectionService } from './core/security/runtime-protection.service';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import enTranslations from '../../public/i18n/en.json';

const chunkReloadStorageKey = 'gtb-next-portal:chunk-reload-url';

const isChunkLoadError = (error: unknown): boolean => {
  const message = error instanceof Error
    ? `${error.name} ${error.message}`
    : String(error ?? '');

  return /ChunkLoadError|Loading chunk \d+ failed|Failed to fetch dynamically imported module|Importing a module script failed|module script.*MIME type/i.test(message);
};

const toAppUrl = (routerUrl: string): string => {
  const basePath = new URL(document.baseURI).pathname.replace(/\/$/, '');
  const relativeUrl = routerUrl.replace(/^\//, '');

  return `${basePath}/${relativeUrl}`;
};

const recoverFromChunkLoadError = (event: NavigationError): void => {
  if (!isChunkLoadError(event.error)) {
    throw event.error;
  }

  const targetUrl = toAppUrl(event.url);
  const previousReloadUrl = sessionStorage.getItem(chunkReloadStorageKey);

  if (previousReloadUrl === targetUrl) {
    sessionStorage.removeItem(chunkReloadStorageKey);
    throw event.error;
  }

  sessionStorage.setItem(chunkReloadStorageKey, targetUrl);
  window.location.assign(targetUrl);
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideHttpClient(withInterceptors([authInterceptor, loadingInterceptor])),
    provideRouter(
      appRoutes,
      // Route params arrive as component inputs, so a detail page reads `:id` without ActivatedRoute.
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
      withNavigationErrorHandler(recoverFromChunkLoadError)
    ),
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'dd/MM/yyyy',
        },
        display: {
          dateInput: 'dd/MM/yyyy',
          monthYearLabel: 'LLL yyyy',
          dateA11yLabel: 'PP',
          monthYearA11yLabel: 'LLLL yyyy',
        },
      },
    },
    provideTransloco({
      config: {
        availableLangs: [
          {
            id: 'en',
            label: 'English',
          },
          {
            id: 'th',
            label: 'ไทย',
          },
        ],
        defaultLang: 'en',
        fallbackLang: 'th',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideAppInitializer(() => {
      const translocoService = inject(TranslocoService);
      const defaultLang = translocoService.getDefaultLang();

      translocoService.setTranslation(enTranslations, defaultLang);
      translocoService.setActiveLang(defaultLang);
    }),
    provideAppInitializer(() => {
      const runtimeProtectionService = inject(RuntimeProtectionService);
      runtimeProtectionService.initialize();
      sessionStorage.removeItem(chunkReloadStorageKey);
    }),
    provideAppInitializer(() => {
      const appVersionService = inject(AppVersionService);
      appVersionService.start();
    }),
    provideAuth(),
    provideIcons(),
    provideFuse({
      fuse: {
        layout: 'classy',
        scheme: 'light',
        screens: {
          sm: '600px',
          md: '960px',
          lg: '1280px',
          xl: '1440px',
        },
        theme: 'theme-default',
        themes: [
          {
            id: 'theme-default',
            name: 'Default',
          },
          {
            id: 'theme-teal',
            name: 'Teal',
          },
          {
            id: 'theme-rose',
            name: 'Rose',
          },
          {
            id: 'theme-purple',
            name: 'Purple',
          },
          {
            id: 'theme-amber',
            name: 'Amber',
          },
        ],
      },
    }),
    provideToastr({
      timeOut: 3000,
      positionClass: 'toast-top-right',
      progressBar: true,
      preventDuplicates: true,
      closeButton: true
    })
  ]
};
